import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getBenefitHasAvailableUses } from '@/utils/clubBenefitUsage';
import type { ClubProcessedBenefit } from '@/utils/clubMonthlySummary';

function capitalizeFirst(str: string): string {
	if (!str || typeof str !== 'string') return str;
	const i = str.search(/[a-zA-ZÀ-ÿ]/);
	if (i === -1) return str;
	return str.slice(0, i) + str[i].toUpperCase() + str.slice(i + 1);
}

type ClubConsumableBenefitsSectionProps = {
	benefits: ClubProcessedBenefit[];
	onUseBenefit?: (benefit: ClubProcessedBenefit) => void;
	onRedeemBenefit?: (benefit: ClubProcessedBenefit) => void;
	className?: string;
	columns?: 2 | 3;
};

const gridColsClassName: Record<2 | 3, string> = {
	2: 'grid-cols-1 md:grid-cols-2',
	3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
};

const ClubConsumableBenefitsSection = ({
	benefits,
	onUseBenefit,
	onRedeemBenefit,
	className,
	columns = 2,
}: ClubConsumableBenefitsSectionProps) => {
	if (benefits.length === 0) {
		return null;
	}

	return (
		<div className={cn('w-full space-y-6', className)}>
			<div className="flex items-baseline gap-2">
				<h2 className="text-2xl font-bold text-slate-900">
					Seus Benefícios
				</h2>
				<span className="text-lg font-medium text-slate-400">
					({benefits.length})
				</span>
			</div>

			<div
				className={cn('grid w-full gap-6', gridColsClassName[columns])}
			>
				{benefits.map((benefit) => (
					<div
						key={benefit.id}
						className="flex h-full min-h-[220px] flex-col space-y-6 rounded-[32px] border border-slate-100 bg-white p-6 shadow-sm"
					>
						<div className="flex items-start justify-between">
							<div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange">
								<Check size={20} />
							</div>
							<div className="text-right">
								<div className="mb-1 text-[9px] font-bold uppercase tracking-widest text-slate-400">
									Status de Uso
								</div>
								<div className="text-lg font-bold text-slate-900">
									{benefit.renewalLabel ===
									'Sempre Disponível'
										? ''
										: `${benefit.used} / ${benefit.total}`}
								</div>
								{benefit.hasUsageCredit ? (
									<div className="mt-1 text-[9px] font-bold uppercase tracking-wider text-emerald-700">
										Crédito de uso
									</div>
								) : null}
							</div>
						</div>

						<div className="space-y-4">
							<div className="inline-block rounded bg-slate-100 px-2 py-1 text-[9px] font-bold uppercase tracking-widest text-slate-500">
								{benefit.renewalLabel}
							</div>
							<div className="space-y-1">
								<h3 className="line-clamp-2 font-bold leading-tight text-slate-900">
									{capitalizeFirst(benefit.title)}
								</h3>
								<p className="line-clamp-2 text-xs font-medium text-slate-400">
									{capitalizeFirst(benefit.description)}
								</p>
							</div>

							{benefit.renewalLabel !== 'Sempre Disponível' && (
								<div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
									<div
										className="h-full bg-brand-orange transition-all duration-500"
										style={{
											width: `${benefit.progressPercentage}%`,
										}}
									/>
								</div>
							)}
						</div>

						{benefit.renewalLabel !== 'Sempre Disponível' && (
							<div className="border-t border-slate-50 pt-4">
								<div className="flex items-center justify-between gap-3">
									<div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
										Restantes:{' '}
										<span
											className={cn(
												benefit.hasUsageCredit
													? 'text-emerald-700'
													: 'text-slate-900',
											)}
										>
											{benefit.remaining}
											{benefit.remaining > benefit.total
												? ` (de ${benefit.total})`
												: ''}
										</span>
									</div>
									{benefit.serviceType === 'PROFESSION' &&
										getBenefitHasAvailableUses(
											benefit.total,
											benefit.used,
										) &&
										onUseBenefit && (
											<button
												type="button"
												onClick={() =>
													onUseBenefit(benefit)
												}
												className="rounded-lg border border-brand-orange/35 bg-brand-orange/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-brand-orange transition-colors hover:bg-brand-orange/15"
											>
												Usar benefício
											</button>
										)}
								</div>
							</div>
						)}
						{benefit.serviceType === 'MOBILE_APP' &&
							onRedeemBenefit && (
								<div className="border-t border-slate-50 pt-4">
									<button
										type="button"
										onClick={() => onRedeemBenefit(benefit)}
										className="w-full rounded-xl border border-brand-orange/35 bg-brand-orange/10 px-4 py-2.5 text-sm font-semibold text-brand-orange transition-colors hover:bg-brand-orange/15"
									>
										Resgatar benefício
									</button>
								</div>
							)}
					</div>
				))}
			</div>
		</div>
	);
};

export { ClubConsumableBenefitsSection };
